<script setup lang="ts">
/**
 * "ยังไม่ได้กำหนดคำตอบเรื่อง ราคา, ค่าส่ง" (owner 17 ต.ค. 69: "ต้อง fix เลยคำถามประเภทนี้ต้องตอบอะไรกำหนดโดยฉันเอง"):
 * every suggestion is the owner's own answer from the answer library, so a question type without an answer switched on
 * gets no suggested text — this line says which ones. Owner / head: a button to ตั้งค่า › คำตอบสำเร็จรูป (the types
 * passed along so the page can point at them); staff: who can define them. Nothing here inserts any text.
 */
import { computed, inject } from 'vue'
import { routerKey } from 'vue-router'
import type { ScriptUndefinedTopic } from '../../api/script/types'
import { undefinedLine } from '../../lib/script'
import { useSession } from '../../stores/session'
import Icon from '../ui/Icon.vue'

const props = defineProps<{ topics: ScriptUndefinedTopic[]; compact?: boolean }>()
const session = useSession()
const router = inject(routerKey, null)
const line = computed(() => undefinedLine(props.topics))
const canDefine = computed(() => session.me?.role === 'owner' || session.me?.role === 'head')

function open(): void {
  const need = [...new Set(props.topics.map((t) => t.key))].join(',')
  if (router) void router.push({ name: 'config', params: { section: 'canned' }, query: { need } })
  else window.location.assign(`${import.meta.env.BASE_URL}config/canned?need=${encodeURIComponent(need)}`)
}
</script>

<template>
  <div v-if="line" class="und" :class="{ compact }" role="note">
    <Icon name="info" />
    <span class="und-t">{{ line }}</span>
    <button v-if="canDefine" type="button" class="und-b" @click="open">กำหนดคำตอบ<Icon name="arrowRight" /></button>
    <span v-else class="und-m">เจ้าของหรือผู้จัดการกำหนดได้ที่คลังคำตอบ</span>
  </div>
</template>

<style scoped>
.und {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 4px 8px;
  min-height: 38px;
  padding: 6px 8px 6px 12px;
  border-bottom: 1px solid #e3ece7;
  font-size: 12.5px;
  line-height: 19px;
  color: #6b5a2e;
  background: #fffaf0;
  border-radius: 11px 11px 0 0;
}
.und.compact {
  min-height: 0;
  margin-top: 8px;
  padding: 6px 8px 6px 10px;
  border: 1px solid #f0e2c4;
  border-radius: 8px;
}
.und > :deep(.ic) {
  flex: none;
  width: 14px;
  height: 14px;
  color: #b8740a;
}
.und-t {
  flex: 1 1 200px;
  min-width: 0;
  font-weight: 600;
}
.und-m {
  font-size: 12px;
  color: var(--mut);
}
.und-b {
  flex: none;
  height: 28px;
  padding: 0 10px;
  border-radius: 8px;
  border: 1px solid #e6cf9f;
  background: #fff;
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-size: 12.5px;
  font-weight: 600;
  line-height: 19px;
  color: #6b4a06;
  white-space: nowrap;
}
.und-b:hover {
  background: #fdf3df;
}
.und-b :deep(.ic) {
  width: 14px;
  height: 14px;
}
</style>
